// src/components/Objects.jsx

import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { FaArrowLeft, FaFolder, FaFolderPlus } from 'react-icons/fa'
import { RiUploadCloud2Fill } from "react-icons/ri";
import { Button, Spinner, Pagination, ButtonGroup, ToggleButton } from 'react-bootstrap'
import axios from 'axios'
import { toast } from 'react-toastify'

import { sortFn } from '../utils/sort'
import FileIcon from './FileIcon'
import ActionDropDown from './ActionDropDown'
import useUploadQueue from '../hooks/useUploadQueue'
import FileSelector from './FileSelector'
import CreateFolderModal from './modals/CreateFolderModal'
import ShareLinkModal from './modals/ShareLinkModal'
import ObjectDetailsModal from './modals/ObjectDetailsModal'
import ConfirmDeleteModal from './modals/ConfirmDeleteModal'
import ConfirmMultiDeleteModal from './modals/ConfirmMultiDeleteModal'
import EditObjectModal from './modals/EditObjectModal'
import PreviewModal from './modals/PreviewModal'
import PublishFolderModal from './modals/PublishFolderModal'
import { formatSize } from '../utils/formatSize'
import './Objects.css'

const PUBLIC_API_BASE = 'https://api.viadrive.com'; // host público p/ preview PDF

// Extensões para grid
const IMAGE_EXT = new Set(["jpg","jpeg","png","gif","webp","bmp","avif","tif","tiff","svg"])
const VIDEO_EXT = new Set(["mp4","m4v","webm","mov","mkv","avi"])

// cache simples em memória para URLs assinadas de preview
const previewUrlCache = new Map()

function parseSharedFlag(value) {
  if (value === true || value === 1) return true
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase()
    return normalized === 'true' || normalized === '1' || normalized === 'yes'
  }
  return false
}

function formatDateTime(value) {
  if (!value) return 'N/A'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return 'N/A'
  return date.toLocaleString('pt-BR')
}

function formatObjectLabel(key, currentPath) {
  return key.replace(currentPath, '')
}

function canInlinePreview(key) {
  return /\.(jpg|jpeg|png|gif|webp|bmp|avif|tif|tiff|svg|pdf)$/i.test(key || '')
}

function isInspectorSelection(inspectorItem, candidate) {
  if (!inspectorItem || !candidate) return false
  if (inspectorItem.type === 'folder' && candidate.type === 'folder') {
    return inspectorItem.key === candidate.key
  }
  if (inspectorItem.type === 'file' && candidate.type === 'file') {
    return inspectorItem.obj?.Key === candidate.obj?.Key
  }
  return false
}

export default function Objects() {
  const { bucketName } = useParams()
  const navigate = useNavigate()
  const apiToken = localStorage.getItem('token')

  // STATES
  const [prefixes, setPrefixes] = useState([])
  const [objects, setObjects] = useState([])
  const [currentPath, setCurrentPath] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [sortField, setSortField] = useState('name')
  const [sortDirection, setSortDirection] = useState('asc')
  const [selectedKeys, setSelectedKeys] = useState([])
  const [loading, setLoading] = useState(false)
  const [pageSize, setPageSize] = useState(50)
  const [currentPage, setCurrentPage] = useState(1)

  // View mode (lista | grid)
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('vd_viewMode') || 'list')
  useEffect(() => { localStorage.setItem('vd_viewMode', viewMode) }, [viewMode])

  // MODALS & SELECTION
  const [showFileSelector, setShowFileSelector] = useState(false)
  const [showFolderModal, setShowFolderModal] = useState(false)
  const [showShareModal, setShowShareModal] = useState(false)
  const [shareObjectKey, setShareObjectKey] = useState(null)
  const [showDetailsModal, setShowDetailsModal] = useState(false)
  const [selectedObject, setSelectedObject] = useState(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [itemToDelete, setItemToDelete] = useState(null)
  const [showMultiDeleteModal, setShowMultiDeleteModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [objectToEdit, setObjectToEdit] = useState(null)
  const [showPreview, setShowPreview] = useState(false)
  const [previewObj, setPreviewObj] = useState(null)
  const [publishModal, setPublishModal] = useState({ show: false, bucket: '', prefix: '' })
  const [inspectorItem, setInspectorItem] = useState(null)
  const [inspectorPreviewUrl, setInspectorPreviewUrl] = useState('')
  const [inspectorPreviewLoading, setInspectorPreviewLoading] = useState(false)

  // ==== Upload hook (SharedWorker) ====
  const {
    items,
    addFiles,
    cancelUpload,
    clearUploads
  } = useUploadQueue(bucketName, currentPath)

  // FETCH OBJECTS
  const fetchObjects = useCallback(async () => {
    setLoading(true)
    try {
      const res = await axios.get(
        `${import.meta.env.VITE_BACKEND_URL}/api/buckets/${bucketName}/objects`,
        {
          params: { prefix: currentPath, maxKeys: 1000 },
          headers: { Authorization: `Bearer ${apiToken}` }
        }
      )
      setPrefixes(res.data.prefixos || [])
      setObjects(
        (res.data.objetos || []).map(o => ({
          ...o,
          shared: parseSharedFlag(o.isShared)
        }))
      )
      setCurrentPage(1)
    } catch {
      toast.error('Falha ao carregar objetos')
    } finally {
      setLoading(false)
    }
  }, [bucketName, currentPath, apiToken])

  useEffect(() => {
    fetchObjects()
    setSelectedKeys([])
  }, [fetchObjects])

  useEffect(() => {
    setInspectorItem(null)
    setInspectorPreviewUrl('')
    setInspectorPreviewLoading(false)
  }, [currentPath, bucketName])

  // Helpers
  const isPdf = (key) => /\.pdf$/i.test(key)

  // Quando houver pelo menos 1 concluído na fila, recarrega listagem
  const doneCount = items.filter(i => i.status === 'completed').length
  useEffect(() => {
    if (doneCount > 0) fetchObjects()
  }, [doneCount, fetchObjects])

  // ACTION HANDLERS
  const handleDownload = async key => {
    try {
      const { data } = await axios.get(
        `${import.meta.env.VITE_BACKEND_URL}/api/share/${bucketName}/${encodeURIComponent(key)}?expires=300`,
        { headers: { Authorization: `Bearer ${apiToken}` } }
      )
      const filename = key.split('/').pop() || 'download'

      try {
        const fileRes = await axios.get(data.url, { responseType: 'blob' })
        const blobUrl = window.URL.createObjectURL(fileRes.data)
        const link = document.createElement('a')
        link.href = blobUrl
        link.download = filename
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(blobUrl)
      } catch (downloadErr) {
        console.error('Fallback download via nova aba:', downloadErr)
        const link = document.createElement('a')
        link.href = data.url
        link.target = '_blank'
        link.rel = 'noopener noreferrer'
        document.body.appendChild(link)
        link.click()
        link.remove()
      }
    } catch {
      toast.error('Falha ao gerar link de download')
    }
  }

  const handleCopyLink = async (objectKey) => {
    try {
      const jwt = localStorage.getItem("token")
      const baseUrl = `${import.meta.env.VITE_BACKEND_URL}/api/share/${bucketName}/${encodeURIComponent(objectKey)}`
      const params = new URLSearchParams({ expires: '0', short: "1", ui: "1" })

      const { data } = await axios.get(`${baseUrl}?${params}`, {
        headers: { Authorization: `Bearer ${jwt}` },
      })

      let finalUrl = data?.url

      if (!data?.short && finalUrl) {
        const { data: s } = await axios.post(
          `${import.meta.env.VITE_BACKEND_URL}/api/share/shorten-link`,
          { longUrl: finalUrl }
        )
        finalUrl = s.shortUrl || finalUrl
      }

      if (!finalUrl) throw new Error("Resposta sem URL.")

      await navigator.clipboard.writeText(finalUrl)
      toast.success("Link encurtado copiado!")
    } catch (err) {
      console.error("Erro ao copiar link:", err)
      toast.error("Erro ao copiar link.")
    }
  }

  const handleRevoke = async key => {
    try {
      await axios.delete(
        `${import.meta.env.VITE_BACKEND_URL}/api/share/${bucketName}/${encodeURIComponent(key)}/public`,
        { headers: { Authorization: `Bearer ${apiToken}` } }
      )
      toast.success('Compartilhamento revogado')
      fetchObjects()
    } catch {
      toast.error('Falha ao revogar compartilhamento')
    }
  }

  const handleDelete = (key, isFolder = false) => {
    setItemToDelete({ key, isFolder })
    setShowDeleteModal(true)
  }

  const handleConfirmDelete = async () => {
    const { key, isFolder } = itemToDelete
    const endpoint = isFolder
      ? `/api/buckets/${bucketName}/folder/delete`
      : `/api/buckets/${bucketName}/objects/delete`
    const body = isFolder ? { prefix: key } : { key }
    try {
      await axios.post(
        `${import.meta.env.VITE_BACKEND_URL}${endpoint}`,
        body,
        { headers: { Authorization: `Bearer ${apiToken}` } }
      )
      toast.success('Item excluído')
      fetchObjects()
    } catch {
      toast.error('Falha ao excluir')
    } finally {
      setShowDeleteModal(false)
    }
  }

  const handleMultiDelete = async () => {
    if (!selectedKeys.length) return
    try {
      await axios.post(
        `${import.meta.env.VITE_BACKEND_URL}/api/buckets/${bucketName}/objects/multi-delete`,
        { keys: selectedKeys },
        { headers: { Authorization: `Bearer ${apiToken}` } }
      )
      toast.success('Objetos excluídos!')
      fetchObjects()
      setSelectedKeys([])
    } catch {
      toast.error('Erro ao excluir múltiplos objetos')
    } finally {
      setShowMultiDeleteModal(false)
    }
  }

  const handleEdit = obj => { setObjectToEdit(obj); setShowEditModal(true) }
  const handleShowDetails = obj => {
    setSelectedObject(obj)
    setShowDetailsModal(true)
  }

  // Preview: PDF abre em nova aba via endpoint público com redirect=1
const handlePreview = async (obj) => {
  const bucket = bucketName;
  const key = obj?.Key;
  const token = localStorage.getItem("token");
  const expires = 3600; // 1 hora

  try {
    const res = await axios.get(
      `${import.meta.env.VITE_BACKEND_URL}/api/share/${bucket}/${encodeURIComponent(key)}?expires=${expires}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );

    const url = res.data.url;
    window.open(url, "_blank");
  } catch (err) {
    console.error("Erro ao abrir preview (igual download):", err);
    toast.error("Não foi possível abrir o preview.");
  }
};

  const handleShare = key => { setShareObjectKey(key); setShowShareModal(true) }
  const handlePublishFolder = obj =>
    setPublishModal({ show: true, bucket: bucketName, prefix: obj.Key })

  // SELECTION
  const toggleSelection = key =>
    setSelectedKeys(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    )

  // SORT, FILTER & PAGINATE
  const filteredP = prefixes.filter(p =>
    p.toLowerCase().includes(searchTerm.toLowerCase())
  )
  const filteredO = objects
    .filter(o => !o.Key.endsWith('/'))
    .filter(o => o.Key.toLowerCase().includes(searchTerm.toLowerCase()))
  const sortedP = [...filteredP].sort((a, b) =>
    sortFn({ Key: a }, { Key: b }, sortField, sortDirection)
  )
  const sortedO = [...filteredO].sort((a, b) =>
    sortFn(a, b, sortField, sortDirection)
  )
  const combinedAll = [
    ...sortedP.map(k => ({ type: 'folder', key: k, shared: false })),
    ...sortedO.map(o => ({ type: 'file', obj: o }))
  ]
  const totalFolders = sortedP.length
  const totalFiles = sortedO.length
  const totalFileBytes = sortedO.reduce((acc, item) => acc + (item.Size || 0), 0)
  const latestFile = sortedO.length
    ? [...sortedO].sort(
        (a, b) => new Date(b.LastModified).getTime() - new Date(a.LastModified).getTime()
      )[0]
    : null
  const totalPages = Math.max(1, Math.ceil(combinedAll.length / pageSize))
  const combined = combinedAll.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  )

  // BREADCRUMB
  const segments = currentPath.split('/').filter(Boolean)
  const parentPath =
    segments.length > 1
      ? segments.slice(0, segments.length - 1).join('/') + '/'
      : ''

  const toggleSelectAll = () => {
    const keys = combined.map(i => (i.type === 'folder' ? i.key : i.obj.Key))
    setSelectedKeys(prev => (prev.length === keys.length ? [] : keys))
  }

  // === helper para buscar URL de preview assinada (inline)
  const getPreviewUrl = useCallback(async (key, opts={ videoSeconds: 0 }) => {
    const cacheKey = `${bucketName}|${key}|inline|${opts.videoSeconds||0}`
    const hit = previewUrlCache.get(cacheKey)
    if (hit) return hit
    const qs = new URLSearchParams({ expires: '600', inline: '1', redirect: '0' })
    if (opts.videoSeconds) qs.set('pv', String(opts.videoSeconds)) // se você implementar no backend
    const { data } = await axios.get(
      `${import.meta.env.VITE_BACKEND_URL}/api/share/${bucketName}/${encodeURIComponent(key)}?${qs}`,
      { headers: { Authorization: `Bearer ${apiToken}` } }
    )
    if (!data?.url) throw new Error('Sem URL')
    previewUrlCache.set(cacheKey, data.url)
    return data.url
  }, [bucketName, apiToken])

  useEffect(() => {
    let active = true

    async function loadInspectorPreview() {
      if (inspectorItem?.type !== 'file' || !canInlinePreview(inspectorItem.obj?.Key)) {
        setInspectorPreviewUrl('')
        setInspectorPreviewLoading(false)
        return
      }

      setInspectorPreviewLoading(true)
      try {
        const url = await getPreviewUrl(inspectorItem.obj.Key)
        if (active) setInspectorPreviewUrl(url)
      } catch (err) {
        console.error('Erro ao carregar preview lateral:', err)
        if (active) setInspectorPreviewUrl('')
      } finally {
        if (active) setInspectorPreviewLoading(false)
      }
    }

    loadInspectorPreview()
    return () => { active = false }
  }, [inspectorItem, getPreviewUrl])

  // === RENDERERS ===
const renderList = () => (
  <div className="table-responsive object-table-shell">
    <table className="table object-table">
      <thead>
        <tr>
          <th>
            <input
              type="checkbox"
              onChange={toggleSelectAll}
              checked={selectedKeys.length === combined.length && combined.length > 0}
            />
          </th>
          <th
            onClick={() => { setSortField('name'); setSortDirection(d => (d === 'asc' ? 'desc' : 'asc')) }}
            style={{ cursor: 'pointer' }}
          >
            Nome {sortField === 'name' ? (sortDirection === 'asc' ? '▲' : '▼') : ''}
          </th>
          <th
            className="text-center"
            onClick={() => { setSortField('date'); setSortDirection(d => (d === 'asc' ? 'desc' : 'asc')) }}
            style={{ cursor: 'pointer' }}
          >
            Últ. Mod. {sortField === 'date' ? (sortDirection === 'asc' ? '▲' : '▼') : ''}
          </th>
          <th
            className="text-end"
            onClick={() => { setSortField('size'); setSortDirection(d => (d === 'asc' ? 'desc' : 'asc')) }}
            style={{ cursor: 'pointer' }}
          >
            Tamanho {sortField === 'size' ? (sortDirection === 'asc' ? '▲' : '▼') : ''}
          </th>
          <th className="text-center">Ações</th>
        </tr>
      </thead>

      <tbody>
        {loading ? (
          <tr>
            <td colSpan={5} className="text-center">
              <Spinner />
            </td>
          </tr>
        ) : combined.length === 0 ? (
          <tr>
            <td colSpan={5} className="text-center text-muted">
              Nenhum item encontrado.
            </td>
          </tr>
        ) : (
          combined.map(item =>
            item.type === 'folder' ? (
              <tr
                key={item.key}
                className={isInspectorSelection(inspectorItem, item) ? 'is-selected' : ''}
              >
                <td>
                  <input
                    type="checkbox"
                    checked={selectedKeys.includes(item.key)}
                    onChange={() => toggleSelection(item.key)}
                  />
                </td>
                <td
                  onClick={() => {
                    setInspectorItem({ type: 'folder', key: item.key })
                  }}
                  onDoubleClick={() => setCurrentPath(item.key)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="object-name-cell">
                    <span className="object-icon-wrap">
                      <FileIcon extension="folder" />
                    </span>
                    <div className="object-name-copy">
                      <div className="object-name-primary">
                        {formatObjectLabel(item.key, currentPath)}
                      </div>
                      <div className="object-name-secondary">
                        <span className="object-type-pill folder">Pasta</span>
                      </div>
                    </div>
                  </div>
                </td>
                <td className="text-center">—</td>
                <td className="text-end">—</td>
                <td className="text-center">
                  <ActionDropDown
                    object={{ Key: item.key, Shared: item.shared }}
                    onDownload={() => handleDownload(item.key)}
                    onShare={() => handleShare(item.key)}
                    onCopyLink={() => handleCopyLink(item.key)}
                    onRevoke={() => handleRevoke(item.key)}
                    onPublishFolder={() => handlePublishFolder({ Key: item.key })}
                    onDelete={() => handleDelete(item.key, true)}
                  />
                </td>
              </tr>
            ) : (
              <tr
                key={item.obj.Key}
                className={isInspectorSelection(inspectorItem, item) ? 'is-selected' : ''}
              >
                <td>
                  <input
                    type="checkbox"
                    checked={selectedKeys.includes(item.obj.Key)}
                    onChange={() => toggleSelection(item.obj.Key)}
                  />
                </td>
                <td
                  onClick={() => {
                    setInspectorItem({ type: 'file', obj: item.obj })
                  }}
                  onDoubleClick={() => {
                    handlePreview(item.obj)
                  }}
                  style={{ cursor: 'pointer', wordBreak: 'break-word' }}
                >
                  <div className="object-name-cell">
                    <span className="object-icon-wrap">
                      <FileIcon extension={(item.obj.Key.split('.').pop() || '').toLowerCase()} />
                    </span>
                    <div className="object-name-copy">
                      <div className="object-name-primary">
                        {formatObjectLabel(item.obj.Key, currentPath)}
                      </div>
                      <div className="object-name-secondary">
                        <span className="object-type-pill file">
                          {((item.obj.Key.split('.').pop() || '').toLowerCase()) || 'arquivo'}
                        </span>
                        {item.obj.shared && <span className="object-shared-pill">Compartilhado</span>}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="text-center">
                  {new Date(item.obj.LastModified).toLocaleString('pt-BR')}
                </td>
                <td className="text-end">{formatSize(item.obj.Size)}</td>
                <td className="text-center">
                  <ActionDropDown
                    object={item.obj}
                    onDetails={() => handleShowDetails(item.obj)}
                    onPreview={() => handlePreview(item.obj)}
                    onDownload={() => handleDownload(item.obj.Key)}
                    onShare={() => handleShare(item.obj.Key)}
                    onCopyLink={() => handleCopyLink(item.obj.Key)}
                    onRevoke={() => handleRevoke(item.obj.Key)}
                    onEdit={() => handleEdit(item.obj)}
                    onDelete={() => handleDelete(item.obj.Key, false)}
                  />
                </td>
              </tr>
            )
          )
        )}
      </tbody>
    </table>
  </div>
)

  const renderGrid = () => (
    <div className="grid-container">
      {loading ? (
        <div className="text-center p-5"><Spinner /></div>
      ) : combined.map(item => (
        <GridItem
          key={item.type === 'folder' ? `f:${item.key}` : `o:${item.obj.Key}`}
          item={item}
          currentPath={currentPath}
          setCurrentPath={setCurrentPath}
          handleDownload={handleDownload}
          handleShare={handleShare}
          handleCopyLink={handleCopyLink}
          handleRevoke={handleRevoke}
          handlePublishFolder={handlePublishFolder}
          handleDelete={handleDelete}
          setSelectedObject={setSelectedObject}
          setShowDetailsModal={setShowDetailsModal}
          handlePreview={handlePreview}
          bucketName={bucketName}
          getPreviewUrl={getPreviewUrl}
          setInspectorItem={setInspectorItem}
          inspectorItem={inspectorItem}
          handleShowDetails={handleShowDetails}
        />
      ))}
    </div>
  )

  return (
    <div className="objects-page">
      <section className="objects-hero">
        <div className="objects-hero-copy">
          <span className="objects-chip">Object Explorer</span>
          <h1>{bucketName}</h1>
          <p>
            Navegue por pastas e arquivos, filtre conteúdo e acione uploads ou
            compartilhamentos com uma interface mais clara.
          </p>
        </div>

        <div className="objects-toolbar">
          <div className="objects-search-box">
            <span className="search-icon">
              <svg width="17" height="17" fill="#7a879f" viewBox="0 0 20 20">
                <path d="M8.5 2a6.5 6.5 0 0 1 5.187 10.5l4.156 4.157a1 1 0 0 1-1.414 1.415l-4.157-4.157A6.5 6.5 0 1 1 8.5 2zm0 2a4.5 4.5 0 1 0 0 9a4.5 4.5 0 0 0 0-9z"></path>
              </svg>
            </span>
            <input
              type="text"
              className="search-input"
              placeholder="Pesquisar arquivos ou pastas..."
              value={searchTerm}
              onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1) }}
            />
          </div>

          <div className="objects-toolbar-actions">
            <select
              className="page-size-select"
              value={pageSize}
              onChange={e => { setPageSize(+e.target.value); setCurrentPage(1) }}
            >
              <option value={50}>50 / pág</option>
              <option value={100}>100 / pág</option>
              <option value={200}>200 / pág</option>
            </select>

            <Button className="btn-purple" onClick={() => setShowFileSelector(true)}>
              <RiUploadCloud2Fill className="me-1" />
              Upload
            </Button>
            <Button variant="secondary" className="objects-secondary-btn" onClick={() => setShowFolderModal(true)}>
              <FaFolderPlus className="me-1" />
              Criar Pasta
            </Button>

            <ButtonGroup className="objects-view-toggle">
              <ToggleButton
                id="mode-list"
                type="radio"
                variant={viewMode==='list'?'dark':'outline-secondary'}
                name="viewmode"
                value="list"
                checked={viewMode==='list'}
                onChange={() => setViewMode('list')}
              >
                Lista
              </ToggleButton>
              <ToggleButton
                id="mode-grid"
                type="radio"
                variant={viewMode==='grid'?'dark':'outline-secondary'}
                name="viewmode"
                value="grid"
                checked={viewMode==='grid'}
                onChange={() => setViewMode('grid')}
              >
                Grid
              </ToggleButton>
            </ButtonGroup>
          </div>
        </div>
      </section>

      <section className="objects-stats">
        <article className="object-stat-card">
          <span>Itens visíveis</span>
          <strong>{combinedAll.length}</strong>
        </article>
        <article className="object-stat-card">
          <span>Pastas</span>
          <strong>{totalFolders}</strong>
        </article>
        <article className="object-stat-card">
          <span>Arquivos</span>
          <strong>{totalFiles}</strong>
        </article>
        <article className="object-stat-card">
          <span>Volume dos arquivos</span>
          <strong>{formatSize(totalFileBytes)}</strong>
        </article>
      </section>

      <section className="objects-path-panel">
        <div className="objects-path-header">
          <div>
            <span className="objects-path-label">Local atual</span>
            <strong>{currentPath || `${bucketName}/`}</strong>
          </div>
          <div className="objects-path-meta">
            <span>Último arquivo</span>
            <strong>{latestFile ? latestFile.Key.replace(currentPath, '') : 'N/A'}</strong>
            <small>{formatDateTime(latestFile?.LastModified)}</small>
          </div>
        </div>

        <div className="objects-breadcrumb d-flex align-items-center mb-0">
        <Button
          variant="link"
          className="p-0 me-2"
          onClick={() => {
            if (currentPath) setCurrentPath(parentPath)
            else navigate('/buckets')
            setSelectedKeys([])
          }}
        >
          <FaArrowLeft />
        </Button>
        <FaFolder className="me-1" />
        <Button
          variant="link"
          className="p-0"
          onClick={() => { setCurrentPath(''); setSelectedKeys([]) }}
        >
          Home
        </Button>
        {segments.map((seg, i) => (
          <React.Fragment key={i}>
            <span className="mx-1">/</span>
            <Button
              variant="link"
              className="p-0"
              onClick={() => { setCurrentPath(segments.slice(0, i + 1).join('/') + '/'); setSelectedKeys([]) }}
            >
              {seg}
            </Button>
          </React.Fragment>
        ))}
        </div>
      </section>

      {/* MULTI-SELECT TOOLBAR */}
      {selectedKeys.length > 0 && (
        <div className="objects-selection-bar">
          <strong>{selectedKeys.length} selecionado(s)</strong>
          <div className="d-flex gap-2">
            <Button variant="danger" size="sm" style={{ minWidth: 115 }} onClick={() => setShowMultiDeleteModal(true)}>
              Excluir Selecionados
            </Button>
            <Button variant="outline-secondary" size="sm" style={{ borderColor: '#5720a3', color: '#5720a3', minWidth: 60 }} onClick={() => setSelectedKeys([])}>
              Cancelar
            </Button>
          </div>
        </div>
      )}

      {/* LISTA OU GRID */}
      <section className="objects-content-panel">
        <div className="objects-workspace">
          <div className="objects-main-view">
            {viewMode === 'list' ? renderList() : renderGrid()}
          </div>

          <aside className="objects-inspector">
            <div className="objects-inspector-card">
              <span className="objects-path-label">Painel lateral</span>
              {inspectorItem?.type === 'file' ? (
                <>
                  <div className="inspector-icon">
                    <FileIcon extension={(inspectorItem.obj.Key.split('.').pop() || '').toLowerCase()} />
                  </div>
                  <h4 title={inspectorItem.obj.Key}>
                    {formatObjectLabel(inspectorItem.obj.Key, currentPath)}
                  </h4>
                  <p className="inspector-subtitle">{bucketName}</p>

                  <div className="inspector-meta">
                    <div>
                      <span>Tipo</span>
                      <strong>{((inspectorItem.obj.Key.split('.').pop() || '').toLowerCase()) || 'arquivo'}</strong>
                    </div>
                    <div>
                      <span>Tamanho</span>
                      <strong>{formatSize(inspectorItem.obj.Size)}</strong>
                    </div>
                    <div>
                      <span>Última modificação</span>
                      <strong>{formatDateTime(inspectorItem.obj.LastModified)}</strong>
                    </div>
                    <div>
                      <span>Status</span>
                      <strong>{inspectorItem.obj.shared ? 'Compartilhado' : 'Privado'}</strong>
                    </div>
                  </div>

                  {canInlinePreview(inspectorItem.obj.Key) && (
                    <div className="inspector-preview-block">
                      <div className="inspector-preview-header">
                        <span>Preview lateral</span>
                        <strong>{/\.pdf$/i.test(inspectorItem.obj.Key) ? 'PDF' : 'Imagem'}</strong>
                      </div>
                      <div className="inspector-preview-surface">
                        {inspectorPreviewLoading ? (
                          <div className="inspector-preview-loading">
                            <Spinner animation="border" />
                          </div>
                        ) : inspectorPreviewUrl ? (
                          /\.pdf$/i.test(inspectorItem.obj.Key) ? (
                            <iframe
                              src={inspectorPreviewUrl}
                              title="preview lateral"
                              className="inspector-preview-frame"
                            />
                          ) : (
                            <img
                              src={inspectorPreviewUrl}
                              alt="preview lateral"
                              className="inspector-preview-image"
                            />
                          )
                        ) : (
                          <div className="inspector-preview-empty">
                            Preview indisponível.
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="inspector-actions">
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      onClick={() => {
                        handleShowDetails(inspectorItem.obj)
                      }}
                    >
                      Ver detalhes
                    </Button>
                    <Button size="sm" className="btn-purple" onClick={() => handlePreview(inspectorItem.obj)}>
                      Pré-visualizar
                    </Button>
                    <Button size="sm" variant="secondary" onClick={() => handleDownload(inspectorItem.obj.Key)}>
                      Baixar
                    </Button>
                    <Button size="sm" variant="outline-secondary" onClick={() => handleShare(inspectorItem.obj.Key)}>
                      Compartilhar
                    </Button>
                  </div>
                </>
              ) : inspectorItem?.type === 'folder' ? (
                <>
                  <div className="inspector-icon folder-icon">
                    <FileIcon extension="folder" />
                  </div>
                  <h4 title={inspectorItem.key}>
                    {formatObjectLabel(inspectorItem.key, currentPath)}
                  </h4>
                  <p className="inspector-subtitle">Pasta em {bucketName}</p>

                  <div className="inspector-meta">
                    <div>
                      <span>Tipo</span>
                      <strong>Pasta</strong>
                    </div>
                    <div>
                      <span>Caminho</span>
                      <strong>{inspectorItem.key}</strong>
                    </div>
                  </div>

                  <div className="inspector-actions">
                    <Button size="sm" className="btn-purple" onClick={() => setCurrentPath(inspectorItem.key)}>
                      Abrir pasta
                    </Button>
                    <Button size="sm" variant="outline-secondary" onClick={() => handleShare(inspectorItem.key)}>
                      Compartilhar
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <div className="inspector-icon bucket-icon">
                    <FaFolder />
                  </div>
                  <h4>{bucketName}</h4>
                  <p className="inspector-subtitle">Selecione um arquivo ou pasta para ver detalhes rápidos.</p>

                  <div className="inspector-meta">
                    <div>
                      <span>Itens visíveis</span>
                      <strong>{combinedAll.length}</strong>
                    </div>
                    <div>
                      <span>Página atual</span>
                      <strong>{currentPage} / {totalPages}</strong>
                    </div>
                    <div>
                      <span>Volume dos arquivos</span>
                      <strong>{formatSize(totalFileBytes)}</strong>
                    </div>
                  </div>
                </>
              )}
            </div>
          </aside>
        </div>
      </section>

      {/* PAGINATION */}
      <div className="d-flex justify-content-center mt-3 objects-pagination-wrap">
        <Pagination>
          <Pagination.First disabled={currentPage === 1} onClick={() => setCurrentPage(1)} />
          <Pagination.Prev disabled={currentPage === 1} onClick={() => setCurrentPage(p => Math.max(1, p - 1))} />
          {[...Array(totalPages)].map((_, i) => (
            <Pagination.Item
              key={i}
              active={currentPage === i + 1}
              onClick={() => setCurrentPage(i + 1)}
            >
              {i + 1}
            </Pagination.Item>
          ))}
          <Pagination.Next disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} />
          <Pagination.Last disabled={currentPage === totalPages} onClick={() => setCurrentPage(totalPages)} />
        </Pagination>
      </div>

      {/* FILE SELECTOR & MODALS */}
      <FileSelector
        show={showFileSelector}
        onHide={() => setShowFileSelector(false)}
        addFiles={files => addFiles(files, bucketName, currentPath)}
      />
      <CreateFolderModal
        show={showFolderModal}
        onHide={() => setShowFolderModal(false)}
        bucketName={bucketName}
        currentPath={currentPath}
        refreshObjects={fetchObjects}
      />
      <ShareLinkModal
        show={showShareModal}
        onHide={() => setShowShareModal(false)}
        bucketName={bucketName}
        objectKey={shareObjectKey}
        objectName={shareObjectKey?.split('/').pop()}
        onShareSuccess={fetchObjects}
      />
      <ObjectDetailsModal
        show={showDetailsModal}
        onHide={() => setShowDetailsModal(false)}
        object={selectedObject}
        bucketName={bucketName}
        onDeleteRequest={key => handleDelete(key, false)}
        onShareRequest={key => handleShare(key)}
      />
      <ConfirmDeleteModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
      />
      <ConfirmMultiDeleteModal
        show={showMultiDeleteModal}
        onHide={() => setShowMultiDeleteModal(false)}
        onConfirm={handleMultiDelete}
        selectedKeys={selectedKeys}
      />
      <EditObjectModal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        object={objectToEdit}
        bucketName={bucketName}
        refreshObjects={fetchObjects}
      />
      <PreviewModal
        show={showPreview}
        onHide={() => setShowPreview(false)}
        object={previewObj}
        bucketName={bucketName}
      />
      <PublishFolderModal
        show={publishModal.show}
        onHide={() => setPublishModal({ ...publishModal, show: false })}
        bucket={publishModal.bucket}
        prefix={publishModal.prefix}
      />
    </div>
  )
}

/* ======== SUBCOMPONENTES PARA O GRID ======== */

/** Limita requisições simultâneas de pré-visualização (evita avalanche) */
const MAX_CONCURRENT_PREVIEWS = 6;
let runningPreviews = 0;
const previewQueue = [];
const runNext = () => {
  if (runningPreviews >= MAX_CONCURRENT_PREVIEWS) return;
  const job = previewQueue.shift();
  if (!job) return;
  runningPreviews++;
  job().finally(() => {
    runningPreviews = Math.max(0, runningPreviews - 1);
    runNext();
  });
};
const enqueuePreview = (fn) =>
  new Promise((resolve) => {
    previewQueue.push(() => fn().finally(resolve));
    runNext();
  });

/** Card do Grid (pasta/arquivo) */
function GridItem({
  item, currentPath, setCurrentPath,
  handleDownload, handleShare, handleCopyLink, handleRevoke,
  handlePublishFolder, handleDelete, setSelectedObject, setShowDetailsModal,
  handlePreview, getPreviewUrl, setInspectorItem, inspectorItem, handleShowDetails
}) {
  if (item.type === 'folder') {
    return (
      <div
        className={`obj-card folder ${isInspectorSelection(inspectorItem, item) ? 'is-selected' : ''}`}
        onClick={() => setInspectorItem({ type: 'folder', key: item.key })}
        onDoubleClick={() => setCurrentPath(item.key)}
      >
        <div className="thumb folder-thumb">
          <FileIcon extension="folder" />
        </div>
        <div className="meta">
          <div className="name" title={item.key}>{item.key.replace(currentPath,'')}</div>
          <div className="actions">
            <ActionDropDown
              object={{ Key: item.key, Shared: item.shared }}
              onDownload={() => handleDownload(item.key)}
              onShare={() => handleShare(item.key)}
              onCopyLink={() => handleCopyLink(item.key)}
              onRevoke={() => handleRevoke(item.key)}
              onPublishFolder={() => handlePublishFolder({ Key: item.key })}
              onDelete={() => handleDelete(item.key, true)}
            />
          </div>
        </div>
      </div>
    );
  }

  const o = item.obj;
  const key = o.Key;
  const ext = (key.split('.').pop() || '').toLowerCase();
  const isImage = IMAGE_EXT.has(ext);
  const isVideo = VIDEO_EXT.has(ext);

  return (
    <div className={`obj-card file ${isInspectorSelection(inspectorItem, item) ? 'is-selected' : ''}`}>
      <div
        className={`thumb ${isImage ? 'img-thumb' : isVideo ? 'video-thumb' : 'file-thumb'}`}
        onClick={() => {
          setInspectorItem({ type: 'file', obj: o })
        }}
        onDoubleClick={() => {
          handlePreview(o)
        }}
        title={key}
      >
        {isImage && <ImageThumb k={key} getPreviewUrl={getPreviewUrl} />}
        {isVideo && <HoverVideo k={key} getPreviewUrl={getPreviewUrl} seconds={4} />}
        {!isImage && !isVideo && <FileIcon extension={ext} />}
      </div>
      <div className="meta">
        <div className="name" title={key}>{key.replace(currentPath,'')}</div>
        <div className="right small text-muted">
          {new Date(o.LastModified).toLocaleString('pt-BR')} · {formatSize(o.Size)}
        </div>
        <div className="actions">
          <ActionDropDown
            object={o}
            onDetails={() => handleShowDetails(o)}
            onPreview={() => handlePreview(o)}
            onDownload={() => handleDownload(o.Key)}
            onShare={() => handleShare(o.Key)}
            onCopyLink={() => handleCopyLink(o.Key)}
            onRevoke={() => handleRevoke(o.Key)}
            onEdit={() => {}}
            onDelete={() => handleDelete(o.Key, false)}
          />
        </div>
      </div>
    </div>
  );
}

/** Imagem com lazy, skeleton e fallback */
function ImageThumb({ k, getPreviewUrl }) {
  const [src, setSrc] = useState('');
  const [failed, setFailed] = useState(false);
  const ref = useRef(null);

  const load = async () => {
    try {
      const u = await getPreviewUrl(k);
      setSrc(u);
      setFailed(false);
    } catch (e) {
      console.warn('Thumb IMG URL error:', k, e);
      setFailed(true);
    }
  };

  useEffect(() => {
    let obs;
    const el = ref.current;
    if ('IntersectionObserver' in window && el) {
      obs = new IntersectionObserver(async (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            await enqueuePreview(load);
            obs.disconnect();
          }
        }
      }, { rootMargin: '200px' });
      obs.observe(el);
    } else {
      enqueuePreview(load);
    }
    return () => obs && obs.disconnect();
  }, [k]);

  return (
    <div ref={ref} className="thumb-media">
      {failed ? (
        <div style={{display:'flex',alignItems:'center',justifyContent:'center',width:'100%',height:'100%',color:'#9aa'}}>
          <FileIcon extension={(k.split('.').pop() || '').toLowerCase()} />
        </div>
      ) : src ? (
        <img
          src={src}
          alt=""
          loading="lazy"
          onError={() => {
            console.warn('IMG onError:', k, src);
            setFailed(true);
          }}
        />
      ) : (
        <div className="thumb-skeleton" />
      )}
    </div>
  );
}

/** Vídeo: toca 4s no hover, com fallback */
function HoverVideo({ k, getPreviewUrl, seconds = 4 }) {
  const [src, setSrc] = useState('');
  const [failed, setFailed] = useState(false);
  const vref = useRef(null);
  const timerRef = useRef(null);

  const ensureSrc = async () => {
    if (src || failed) return;
    try {
      const base = await getPreviewUrl(k, { videoSeconds: seconds });
      const withFrag = `${base.split('#')[0]}#t=0,${seconds}`;
      setSrc(withFrag);
    } catch (e) {
      console.warn('Thumb VIDEO URL error:', k, e);
      setFailed(true);
    }
  };

  const playPreview = async () => {
    await enqueuePreview(ensureSrc);
    setTimeout(() => {
      const v = vref.current;
      if (!v || !src || failed) return;
      v.currentTime = 0;
      v.muted = true;
      v.play().catch(() => {});
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        if (v) { v.pause(); v.currentTime = Math.max(seconds - 0.05, 0); }
      }, seconds * 1000);
    }, 50);
  };

  const stopPreview = () => {
    const v = vref.current;
    clearTimeout(timerRef.current);
    if (v) { v.pause(); v.currentTime = 0; }
  };

  return (
    <div className="thumb-media" onMouseEnter={playPreview} onMouseLeave={stopPreview}>
      {failed ? (
        <div style={{display:'flex',alignItems:'center',justifyContent:'center',width:'100%',height:'100%',color:'#9aa'}}>
          <FileIcon extension="mp4" />
        </div>
      ) : src ? (
        <video
          ref={vref}
          playsInline
          muted
          preload="metadata"
          onError={() => { console.warn('VIDEO onError:', k, src); setFailed(true); }}
        >
          <source src={src} />
        </video>
      ) : (
        <div className="thumb-skeleton" />
      )}
    </div>
  );
}
